/**
 * Il runtime dei campi personalizzati (CF02/05–07) — CL04.
 *
 * Tiene insieme tre cose che non devono confondersi: il **contesto** che il
 * server ha autorizzato, la **bozza** locale, e il **salvataggio** differenziale.
 *
 * Le regole che contano, e il motivo:
 *
 * - **`setValue` non manda niente.** Scrivere in un campo non è salvarlo: una
 *   richiesta per battuta renderebbe il pulsante di salvataggio una decorazione,
 *   e ogni tasto premuto una scrittura che l'utente non ha autorizzato.
 * - **La patch contiene solo i campi editabili davvero cambiati.** Il filtro sta
 *   in `changesForSave` e non qui: rimandare la mappa intera sovrascriverebbe i
 *   valori che il chiamante non può vedere con il sottoinsieme che vede.
 * - **Un 409 non butta la bozza.** Il conflitto significa che il server ha una
 *   revisione diversa, non che il lavoro dell'utente sia sbagliato: si conserva
 *   e si propone la riconciliazione, campo per campo.
 * - **I valori revocati spariscono.** Quando un contesto nuovo non include più
 *   un campo, il valore non si tiene "per confronto": mostrarlo mentre l'accesso
 *   viene tolto sarebbe ridivulgarlo.
 * - **`loading` si chiude sempre.** Nel `finally`, non nel percorso felice: una
 *   richiesta fallita che lasciasse `loading` acceso bloccherebbe la UI per
 *   sempre.
 *
 * Il client non calcola nessuna regola autorevole: obbligatorietà, accesso e
 * risultati delle formule arrivano dal contesto autorizzato.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { FieldValue, RuntimeContext, RuntimeField } from "model/CustomFields";
import { customFieldsApi } from "api/customFieldsApi";
import { newFieldId } from "components/custom-fields/fieldTypes";
import {
  acceptContext,
  acceptSavedContext,
  changesForSave,
  conflictDiff,
  createDraft,
  hasChanges as draftHasChanges,
  setValue as setDraftValue,
  type RuntimeDraft,
} from "components/custom-fields/runtimeDraft";

/** Un campo su cui il server e il client non concordano. */
export interface FieldConflict {
  fieldId: string;
  local: FieldValue;
  remote: FieldValue;
}

interface UseCustomFieldsParams {
  /** Il processo esistente. Assente all'avvio: lì si usa `getStartContext`. */
  processId?: string | null;
  /** Il task da cui si sta compilando, se c'è. */
  taskId?: string | null;
  /** Notify the owning list after persisted runtime values change. */
  onSaved?: () => void;
  /**
   * Il contesto di avvio, quando il processo non esiste ancora.
   *
   * All'avvio non c'è nessun processo da aggiornare: i valori si mandano
   * insieme all'avvio del flusso, non con una patch su qualcosa che non c'è.
   */
  startContext?: RuntimeContext | null;
}

interface UseCustomFieldsResult {
  context: RuntimeContext | null;
  getCurrentContext: () => RuntimeContext | null;
  draft: RuntimeDraft | null;
  /** Scrive nella bozza. Non manda niente al server. */
  setValue: (fieldId: string, value: FieldValue) => void;
  save: () => Promise<boolean>;
  loading: boolean;
  error: unknown;
  /** I campi su cui il server ha una versione diversa, dopo un 409. */
  conflicts: FieldConflict[];
  /** Rilegge il contesto. Con `contextId` mantiene la sessione sulle sue regole. */
  reload: (options?: { contextId?: string }) => Promise<void>;
  hasChanges: boolean;
  /** I campi che si possono scrivere. */
  editable: RuntimeField[];
}

const isConflict = (e: unknown): boolean =>
  (e as { response?: { status?: number } })?.response?.status === 409;

export function useCustomFields({
  processId,
  taskId,
  startContext = null,
  onSaved,
}: UseCustomFieldsParams = {}): UseCustomFieldsResult {
  const [context, setContext] = useState<RuntimeContext | null>(startContext);
  const [draft, setDraft] = useState<RuntimeDraft | null>(
    startContext ? createDraft(startContext) : null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [conflicts, setConflicts] = useState<FieldConflict[]>([]);
  const contextRef = useRef(context);
  const draftRef = useRef(draft);
  const savingRef = useRef<Promise<boolean> | null>(null);
  const loadSequence = useRef(0);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  const reload = useCallback(
    async (options: { contextId?: string } = {}) => {
      if (!processId) return;
      const request = ++loadSequence.current;
      setLoading(true);
      setError(null);
      try {
        const next = await customFieldsApi.getContext(processId, {
          taskId: taskId ?? undefined,
          contextId: options.contextId,
        });
        if (request !== loadSequence.current) return;
        contextRef.current = next;
        setContext(next);
        const nextDraft = draftRef.current ? acceptContext(draftRef.current, next) : createDraft(next);
        draftRef.current = nextDraft;
        setDraft(nextDraft);
        // Il contesto è cambiato: i conflitti di quello precedente non
        // descrivono più niente.
        setConflicts([]);
      } catch (e) {
        if (request === loadSequence.current) setError(e);
      } finally {
        if (request === loadSequence.current) setLoading(false);
      }
    },
    [processId, taskId]
  );

  // Il contesto si carica quando cambia il soggetto, non a ogni render: un
  // `contextId` nuovo a ogni apertura invaliderebbe i ticket preparati.
  useEffect(() => {
    if (processId) void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processId, taskId]);

  const setValue = useCallback((fieldId: string, value: FieldValue) => {
    if (!draftRef.current) return;
    const next = setDraftValue(draftRef.current, fieldId, value);
    draftRef.current = next;
    setDraft(next);
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (savingRef.current) {
      const completed = await savingRef.current;
      if (!completed) return false;
    }
    const draft = draftRef.current;
    const context = contextRef.current;
    if (!processId || !draft || !context || context.processId !== processId ||
        (taskId && context.taskId !== taskId)) return false;

    const changes = changesForSave(draft, context.fields);
    // Niente da mandare: una patch vuota è una richiesta che non ha motivo di
    // esistere, e il server la rifiuterebbe comunque.
    if (Object.keys(changes).length === 0) return true;

    const pending = (async () => {
      setLoading(true);
      setError(null);
      try {
      const next = await customFieldsApi.patchValues(processId, {
        operationId: newFieldId(),
        contextId: draft.contextId,
        // Si dichiara la revisione su cui la **bozza** è costruita, non quella
        // che il client crede corrente: sono la stessa cosa finché non arriva un
        // conflitto, ed è quello il momento in cui conta.
        expectedRevision: draft.revision,
        changes,
      });
      contextRef.current = next;
      setContext(next);
      // Keep edits made while the request was in flight; the response only
      // acknowledges the snapshot that was sent.
      const nextDraft = acceptSavedContext(draftRef.current ?? draft, draft, next);
      draftRef.current = nextDraft;
      setDraft(nextDraft);
      setConflicts([]);
      return true;
      } catch (e) {
        setError(e);
        if (isConflict(e)) {
        // La bozza **resta**: il conflitto dice che il server ha una revisione
        // diversa, non che il lavoro dell'utente sia sbagliato. Buttar via
        // quello che ha scritto sarebbe punirlo per un disaccordo fra server.
        setConflicts(conflictDiff(draft, context));
        }
        return false;
      } finally {
        setLoading(false);
      }
    })();
    savingRef.current = pending;
    let saved: boolean;
    try {
      saved = await pending;
    } finally {
      if (savingRef.current === pending) savingRef.current = null;
    }
    // An edit may have arrived during the request. A flush only succeeds once
    // every edit present at completion has been acknowledged by the server.
    if (saved && draftRef.current && contextRef.current &&
        draftHasChanges(draftRef.current, contextRef.current.fields)) return save();
    if (saved) onSavedRef.current?.();
    return saved;
  }, [processId, taskId]);

  return {
    context,
    getCurrentContext: () => contextRef.current,
    draft,
    setValue,
    save,
    loading,
    error,
    conflicts,
    reload,
    hasChanges: draft && context ? draftHasChanges(draft, context.fields) : false,
    editable: context ? context.fields.filter((f) => f.access === "editable") : [],
  };
}

export default useCustomFields;
